import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Cookie } from "lucide-react";
import { EASE } from "./motion";

const KEY = "ul-cookie-consent";

// Google Analytics loads ONLY if a measurement id exists AND consent is granted.
const maybeLoadAnalytics = () => {
  const gaId = process.env.REACT_APP_GA_ID;
  if (!gaId) return;
  if (document.getElementById("ga-script")) return;
  const s = document.createElement("script");
  s.id = "ga-script";
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", gaId, { anonymize_ip: true });
};

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(KEY);
    if (stored === "granted") {
      maybeLoadAnalytics();
    } else if (!stored) {
      const t = setTimeout(() => setVisible(true), 1600);
      return () => clearTimeout(t);
    }
  }, []);

  const decide = (value) => {
    localStorage.setItem(KEY, value);
    setVisible(false);
    if (value === "granted") maybeLoadAnalytics();
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 60 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="fixed inset-x-4 bottom-4 z-[60] mx-auto max-w-3xl border border-white/15 bg-navy/95 p-5 text-white shadow-2xl backdrop-blur-xl sm:p-6"
          role="dialog"
          aria-label="Cookie consent"
          data-testid="cookie-consent-banner"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
            <Cookie size={22} className="hidden shrink-0 text-gold sm:block" />
            <p className="flex-1 text-xs leading-relaxed text-white/70">
              We use essential cookies to run this site, and analytics cookies
              only with your consent. See our{" "}
              <Link to="/privacy-policy" className="gold-underline text-gold">
                Privacy Policy
              </Link>
              .
            </p>
            <div className="flex shrink-0 gap-3">
              <button
                onClick={() => decide("denied")}
                data-testid="cookie-consent-decline-button"
                className="btn-outline-light !px-5 !py-2.5 !text-xs"
              >
                Decline
              </button>
              <button
                onClick={() => decide("granted")}
                data-testid="cookie-consent-accept-button"
                className="btn-gold !px-5 !py-2.5 !text-xs"
              >
                Accept
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
